import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Landmark, Layers, Network, Sparkles, TriangleAlert, UserMinus, UserPlus } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Page,
  PageHeader,
  Pill,
  SearchInput,
  Select,
  SegmentedControl,
  Skeleton,
  Spinner,
  Tabs,
} from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { useFullscreen } from '../hooks/useFullscreen';
import { asOneOf, asSet, useUrlState } from '../hooks/useUrlState';
import { useEntityHistory, useGovernment } from '../features/government/useGovernment';
import { holdersByOrganization, pendingByOrganization } from '../features/government/chart';
import { defaultHeadChoice } from '../features/government/components/EntityPlan';
import { AppliedCard } from '../features/government/components/ApplyOutcome';
import { GovernmentChart } from '../features/government/components/GovernmentChart';
import { RunConsole, RunRow } from '../features/government/components/RunConsole';
import {
  FILTERS,
  StructureTree,
  matchesFilter,
  type TreeFilter,
} from '../features/government/components/StructureTree';
import { InspectorEmpty, NodeInspector } from '../features/government/components/NodeInspector';
import { UpdateCard, UpdatesEmpty } from '../features/government/components/UpdateCard';
import { OutsideList } from '../features/government/components/OutsideList';
import { TimelinePanel } from '../features/government/components/TimelinePanel';
import { CountryDialog, ResearchDialog } from '../features/government/components/Dialogs';
import { plural, treeOf } from '../features/government/format';
import type {
  ApplyResult,
  GovUpdateView,
  HeadChoice,
  NodePlan,
  UpdateKind,
  UpdateStatus,
} from '../features/government/types';

const TABS = ['structure', 'graph', 'updates', 'timeline', 'research'] as const;
type TabValue = (typeof TABS)[number];

const STATUS_FILTERS: Array<{ value: UpdateStatus; label: string }> = [
  { value: 'pending', label: 'Waiting' },
  { value: 'applied', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
];

const KIND_FILTERS: Array<{ value: UpdateKind | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'departure', label: 'Departures' },
  { value: 'correction', label: 'Corrections' },
  { value: 'rename', label: 'Renames' },
  { value: 'restructure', label: 'Reorganisations' },
  { value: 'duplicate', label: 'Duplicates' },
  { value: 'move', label: 'Moves' },
  { value: 'former', label: 'Former holders' },
];

/* One codec per parameter, made once: the URL state reads them on every render */
const TAB = asOneOf(TABS);
const TREE_FILTER = asOneOf(FILTERS.map((option) => option.value));
const UPDATE_KIND = asOneOf(KIND_FILTERS.map((option) => option.value));
const OPEN_NODES = asSet<string>();

/** True while the viewport is wide enough for the side-by-side layout */
const useWideScreen = (): boolean => {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const listen = (event: MediaQueryListEvent) => setWide(event.matches);
    query.addEventListener('change', listen);
    setWide(query.matches);
    return () => query.removeEventListener('change', listen);
  }, []);
  return wide;
};

/** One step of the way from research to records, with what is left to do */

const Step: React.FC<{
  index: number;
  title: string;
  detail: string;
  done?: boolean;
  active?: boolean;
  onClick?: () => void;
}> = ({ index, title, detail, done, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`flex min-w-0 flex-1 items-start gap-2.5 px-3.5 py-3 text-left transition-colors ${
      onClick ? 'hover:bg-neutral-50' : ''
    } ${active ? 'bg-primary-50/40' : ''}`}
  >
    <span
      className={`mt-px inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums ${
        done
          ? 'bg-emerald-50 text-emerald-600'
          : active
            ? 'bg-primary-600 text-white'
            : 'bg-neutral-100 text-neutral-500'
      }`}
    >
      {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : index}
    </span>
    <span className="min-w-0">
      <span className="block truncate text-[12.5px] text-ink">{title}</span>
      <span className="block text-[11.5px] leading-snug text-neutral-400 sm:truncate">{detail}</span>
    </span>
  </button>
);

/**
 * The government map: the state as research finds it, what it means for the
 * directory, and the changes waiting for someone to agree to them.
 */
export const GovernmentPage: React.FC = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'super_admin';
  const government = useGovernment();
  const {
    structure,
    overview,
    updates,
    watcher,
    busy,
    error,
    queue,
    queueing,
    holders,
    pending: pendingList,
    receipts,
    revision,
    recentlyChanged,
    syncedAt,
  } = government;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [applying, setApplying] = useState<{ done: number; total: number } | null>(null);
  const [confirmingApply, setConfirmingApply] = useState(false);
  const [applied, setApplied] = useState<ApplyResult[] | null>(null);
  // What applying everything confirmed would do, from the server, and
  // whether the heads research found are recorded with it
  const [bulkPlans, setBulkPlans] = useState<NodePlan[] | null>(null);
  const [bulkHeads, setBulkHeads] = useState(true);
  // The same for the entity open in the panel
  const [plan, setPlan] = useState<NodePlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  // What applying did to each entity applied from the panel, shown there
  const [outcomes, setOutcomes] = useState<Map<string, ApplyResult>>(() => new Map());
  // The inspector is a panel beside the tree on a wide screen and a sheet on
  // a narrow one; the sheet must not be mounted at all on a wide screen, or
  // its backdrop dims the page behind a panel nobody can see
  const wide = useWideScreen();
  const graphScreen = useFullscreen();

  const nodes = useMemo(() => structure?.nodes ?? [], [structure]);
  // The state as it is: bodies that ended are the timeline's, not the graph's
  const liveNodes = useMemo(() => nodes.filter((node) => !node.ended), [nodes]);
  const { childrenOf } = useMemo(() => treeOf(nodes), [nodes]);

  // What is on screen — the section, the filters, the entity being read — is
  // in the address bar, so a link shows someone else the same thing
  const [tab, setTab] = useUrlState<TabValue>('tab', 'structure', TAB);
  const [filter, setFilter] = useUrlState<TreeFilter>('filter', 'all', TREE_FILTER);
  const [selectedId, setSelectedId] = useUrlState('node', '');
  const [updateKind, setUpdateKind] = useUrlState<UpdateKind | 'all'>('kind', 'all', UPDATE_KIND);
  // The top of the tree stands open, and because that is the default it says
  // nothing in the URL — only what the reader opened themselves does
  const rootsOpen = useMemo(
    () => new Set((childrenOf.get(null) ?? []).map((node) => node.id)),
    [childrenOf],
  );
  const [expanded, setExpanded] = useUrlState<Set<string>>('open', rootsOpen, OPEN_NODES);

  // The box answers every keystroke; the URL catches up once typing stops
  const [query, setQuery] = useUrlState('q', '');
  const [search, setSearch] = useState(query);
  const settledSearch = useDebounce(search, 300);
  useEffect(() => {
    if (settledSearch !== query) setQuery(settledSearch);
  }, [settledSearch, query, setQuery]);

  // An id for an entity that is no longer there selects nothing at all
  const selected = nodes.find((node) => node.id === selectedId) ?? null;

  // Who is in post and what is waiting, by record, for the tree, the panel
  // and the chart alike
  const holdersByOrg = useMemo(() => holdersByOrganization(holders), [holders]);
  const pendingByOrg = useMemo(() => pendingByOrganization(pendingList), [pendingList]);
  const history = useEntityHistory(selected?.organizationId, revision);

  // What is waiting at the open entity, with what was just done there kept
  // in place so the reviewer sees each change land
  const waitingHere = useMemo(() => {
    if (!selected?.organizationId) return [];
    const here = (update: GovUpdateView) => update.organization.id === selected.organizationId;
    const waiting = pendingList.filter(here);
    const reviewed = Array.from(receipts.values()).filter(
      (update) => here(update) && !waiting.some((item) => item.id === update.id),
    );
    return [...waiting, ...reviewed];
  }, [pendingList, receipts, selected?.organizationId]);

  // The plan for the open entity, worked out again whenever the page reads
  // the server — so it always describes what the button will do now
  const selectedId_ = selected?.id;
  useEffect(() => {
    if (!selectedId_ || !canEdit) {
      setPlan(null);
      return;
    }
    let cancelled = false;
    setPlanning(true);
    setPlanError(null);
    government
      .plan([selectedId_])
      .then(([next]) => !cancelled && setPlan(next ?? null))
      .catch((failure: any) => {
        if (!cancelled) {
          setPlan(null);
          setPlanError(failure?.response?.data?.message ?? 'Could not work out what applying would do');
        }
      })
      .finally(() => !cancelled && setPlanning(false));
    return () => {
      cancelled = true;
    };
    // government.plan is stable; the plan is asked for again on each read
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId_, canEdit, revision]);

  const toggle = useCallback(
    (id: string) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setExpanded(next);
    },
    [expanded, setExpanded],
  );

  const select = useCallback(
    (id: string) => {
      setSelectedId(id);
      setSheetOpen(true);
    },
    [setSelectedId],
  );

  const readyIds = useMemo(
    () =>
      nodes
        .filter(
          (node) =>
            node.status === 'confirmed' ||
            (node.status === 'applied' && node.decision.action === 'merge'),
        )
        .filter((node) => node.decision.action !== 'skip')
        .map((node) => node.id),
    [nodes],
  );

  /** What applying everything confirmed would do, added up from the server's plans */
  const bulk = useMemo(() => {
    const plans = (bulkPlans ?? []).filter((item) => !item.blocked && item.record.action !== 'skip');
    const heads = plans.filter(
      (item) => item.head && (item.head.action !== 'recorded' || item.head.closes.length > 0),
    );
    return {
      plans,
      create: plans.filter((item) => item.record.action === 'create').length,
      recreate: plans.filter((item) => item.record.action === 'recreate').length,
      parentsFirst: new Set(plans.flatMap((item) => item.parentsFirst)).size,
      merged: plans.reduce((sum, item) => sum + item.merges.length, 0),
      people: plans.reduce((sum, item) => sum + item.merges.reduce((count, merge) => count + merge.people, 0), 0),
      official: plans.filter((item) => item.rename?.kind === 'official').length,
      corrected: plans.filter((item) => item.rename?.kind === 'correction').length,
      moved: plans.filter((item) => item.parent).length,
      heads,
      newPeople: heads.filter((item) => item.head!.action === 'appoint' && item.head!.person.isNew).length,
      ending: heads.reduce((sum, item) => sum + item.head!.closes.length, 0),
      blocked: (bulkPlans ?? []).filter((item) => item.blocked),
    };
  }, [bulkPlans]);

  const openBulkApply = async () => {
    setConfirmingApply(true);
    setBulkPlans(null);
    try {
      setBulkPlans(await government.plan(readyIds));
    } catch {
      setBulkPlans([]);
    }
  };

  const applyNodes = async (ids: string[], heads?: Record<string, HeadChoice>) => {
    setApplying({ done: 0, total: ids.length });
    const results = await government.apply(ids, {
      heads,
      onProgress: (done, total) => setApplying({ done, total }),
    });
    setApplying(null);
    return results;
  };

  /**
   * Applying from the inspector is the reviewer saying yes to this entity, so
   * it confirms first — the server will not apply an entity nobody reviewed.
   */
  const confirmAndApply = async (id: string, head: HeadChoice | null) => {
    const node = nodes.find((item) => item.id === id);
    if (node?.status === 'proposed') await government.setStatus([id], 'confirmed');
    const results = await applyNodes([id], head ? { [id]: head } : undefined);
    // Said in the panel, where the button was pressed; a parent applied first
    // to carry it is part of the same answer
    const own = results.find((result) => result.nodeId === id);
    if (own) {
      const others = results.filter((result) => result.nodeId !== id);
      setOutcomes((current) => new Map(current).set(id, own));
      if (others.length) setApplied(others);
    } else if (results.length) {
      setApplied(results);
    }
  };

  const confirmVisible = async () => {
    const ids = nodes
      .filter((node) => node.status === 'proposed' && matchesFilter(node, filter, pendingByOrg))
      .map((node) => node.id);
    if (ids.length) await government.setStatus(ids, 'confirmed');
  };

  const visibleUpdates = useMemo(
    () => (updateKind === 'all' ? updates : updates.filter((update) => update.kind === updateKind)),
    [updates, updateKind],
  );

  // Former holders need no choice when the body is known and the person is
  // either matched or plainly new: those can be recorded all at once
  const formerReady = useMemo(
    () =>
      updateKind === 'former' && (government.updateQuery.status ?? 'pending') === 'pending'
        ? visibleUpdates.filter(
            (update) =>
              update.status === 'pending' &&
              // A body that no longer exists is recorded on applying
              (!!update.organization.id || !!update.reorganisation) &&
              (!!update.person?.id || !update.person?.candidates.length),
          )
        : [],
    [updateKind, government.updateQuery.status, visibleUpdates],
  );
  const [formerResult, setFormerResult] = useState<{ applied: number; failed: number } | null>(null);

  const grouped = useMemo(() => {
    const groups = new Map<string, GovUpdateView[]>();
    for (const update of visibleUpdates) {
      const key = update.organization.label || 'Elsewhere';
      groups.set(key, [...(groups.get(key) ?? []), update]);
    }
    const out: Array<[string, GovUpdateView[]]> = [];
    groups.forEach((items, label) => out.push([label, items]));
    return out.sort((a, b) => b[1].length - a[1].length);
  }, [visibleUpdates]);

  const stats = structure?.stats;
  const country = overview?.country ?? structure?.country;
  const pending = overview?.updates.pending ?? 0;

  const meta = country
    ? [
        country.name,
        stats?.total ? plural(stats.total, 'entity', 'entities') : 'nothing mapped yet',
        stats?.applied ? `${stats.applied} in the directory` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  const actions = (
    <>
      {country && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => canEdit && setCountryOpen(true)}
          title={canEdit ? 'Change the country' : `This directory covers ${country.name}`}
        >
          <span className="font-ui-mono text-[11px] uppercase text-neutral-400">{country.code}</span>
          {country.name}
        </Button>
      )}
      {canEdit && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => setResearchOpen(true)}
          title={queueing ? 'Research is running; this will join the queue' : undefined}
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.75} />
          {queueing ? 'Queue research' : 'Research'}
        </Button>
      )}
    </>
  );

  if (government.loading) {
    return (
      <DashboardLayout>
        <Page>
          <Skeleton className="h-7 w-40" />
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
            <Skeleton className="h-96 w-full rounded-2xl" />
            <Skeleton className="hidden h-96 w-full rounded-2xl lg:block" />
          </div>
        </Page>
      </DashboardLayout>
    );
  }

  const inspectorFor = (onClose: () => void, floating = false) =>
    selected && (
    <NodeInspector
      node={selected}
      floating={floating}
      nodes={nodes}
      canEdit={canEdit}
      busy={busy}
      onPatch={(patch) => government.patch(selected.id, patch)}
      onStatus={(status) => void government.setStatus([selected.id], status)}
      onApply={(head) => void confirmAndApply(selected.id, head)}
      queueing={queueing}
      holders={selected.organizationId ? (holdersByOrg.get(selected.organizationId) ?? []) : []}
      waiting={waitingHere}
      history={history}
      plan={plan?.nodeId === selected.id ? plan : null}
      planning={planning}
      planError={planError}
      onApplyUpdate={(id, input) => void government.applyUpdate(id, input)}
      onDismissUpdate={(id) => void government.dismissUpdate(id)}
      onVerifyUpdate={(id) => void government.verifyUpdate(id)}
      onSelectNode={select}
      changed={recentlyChanged.has(selected.id)}
      outcome={outcomes.get(selected.id) ?? null}
      onCloseOutcome={() =>
        setOutcomes((current) => {
          const next = new Map(current);
          next.delete(selected.id);
          return next;
        })
      }


      onResearch={() => {
        void government.start({ kind: 'branch', nodeId: selected.id });
        setSheetOpen(false);
      }}
      onCheckHolders={() => {
        if (selected.organizationId) {
          void government.start({ kind: 'officials', organizationIds: [selected.organizationId] });
          setSheetOpen(false);
        }
      }}
      onClose={onClose}
    />
  );

  const inspector = inspectorFor(() => setSheetOpen(false));

  return (
    <DashboardLayout>
      <Page>
        <PageHeader title="Government" meta={meta} actions={actions} />

        {error && (
          <p role="alert" className="mb-4 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}

        {watcher.run && (
          <RunConsole
            run={watcher.run}
            queue={queue.filter((item) => item.id !== watcher.run?.id)}
            canStop={canEdit}
            onStop={watcher.stop}
            onRemove={canEdit ? (id) => void government.cancelRun(id) : undefined}
            onResume={canEdit ? () => void government.resume(watcher.run!.id) : undefined}
            onDismiss={watcher.dismiss}
            className="mb-4"
          />
        )}

        {applied && (
          <AppliedCard
            results={applied}
            nameOf={(nodeId) => nodes.find((node) => node.id === nodeId)?.name ?? 'An entity'}
            onClose={() => setApplied(null)}
          />
        )}

        <Tabs<TabValue>
          label="Government sections"
          className="mb-5"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'structure', label: 'Structure', count: stats?.total },
            { value: 'graph', label: 'Graph' },
            { value: 'updates', label: 'Updates', count: pending || undefined },
            { value: 'timeline', label: 'Timeline' },
            { value: 'research', label: 'Research', count: overview?.runs.length },
          ]}
        />

        {/* ==================== STRUCTURE ==================== */}
        {tab === 'structure' &&
          (nodes.length === 0 ? (
            <Card className="px-6 py-12 text-center">
              <span className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
                <Landmark className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <h2 className="mt-3 text-[15px] text-ink">
                Map the state of {country?.name ?? 'this country'}
              </h2>
              <p className="mx-auto mt-1.5 max-w-md text-[13px] leading-relaxed text-neutral-500">
                Research reads the official record — decrees, gazettes and the institutions' own sites —
                and proposes the structure, with its sources. Nothing reaches your directory until you
                have agreed to it.
              </p>
              {canEdit && (
                <Button size="sm" variant="primary" className="mt-4" onClick={() => setResearchOpen(true)}>
                  <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                  Start research
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              <Card className="overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-y divide-neutral-200/70 sm:flex sm:divide-y-0">
                  <Step
                    index={1}
                    title="Researched"
                    detail={`${plural(stats?.total ?? 0, 'entity', 'entities')} found`}
                    done
                  />
                  <Step
                    index={2}
                    title="Confirm"
                    detail={
                      stats?.proposed ? `${stats.proposed} not reviewed yet` : 'everything reviewed'
                    }
                    done={!stats?.proposed}
                    active={filter === 'review'}
                    onClick={() => setFilter('review')}
                  />
                  <Step
                    index={3}
                    title="Match"
                    detail={
                      stats?.toMerge
                        ? `${stats.toMerge} would fold duplicates`
                        : `${stats?.matched ?? 0} matched`
                    }
                    active={filter === 'merge'}
                    onClick={() => setFilter('merge')}
                  />
                  <Step
                    index={4}
                    title="Apply"
                    detail={
                      readyIds.length
                        ? `${readyIds.length} ready`
                        : `${stats?.applied ?? 0} in the directory`
                    }
                    done={!readyIds.length && !!stats?.applied}
                    active={filter === 'applied'}
                    onClick={() => setFilter('applied')}
                  />
                  <Step
                    index={5}
                    title="Keep current"
                    detail={pending ? `${plural(pending, 'change')} waiting` : 'nothing waiting'}
                    done={!pending && !!stats?.applied}
                    active={filter === 'changes'}
                    onClick={() => (pending ? setFilter('changes') : setTab('updates'))}
                  />
                </div>
              </Card>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
                <div className="min-w-0 space-y-4">
                  <Card className="overflow-hidden">
                    <div className="border-b border-neutral-200/70 px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <SearchInput
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Search the structure"
                          className="w-full sm:w-56"
                        />
                        <div className="-mx-3 flex max-w-full items-center gap-2 overflow-x-auto px-3 sm:mx-0 sm:px-0">
                          <SegmentedControl
                            label="Show"
                            items={FILTERS}
                            value={filter}
                            onChange={(value) => setFilter(value as TreeFilter)}
                          />
                          {canEdit && stats?.proposed ? (
                            <Button size="xs" variant="ghost" onClick={confirmVisible} disabled={busy}>
                              Confirm these
                            </Button>
                          ) : null}
                          {canEdit && (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => void government.start({ kind: 'curate' })}
                              disabled={busy}
                              title={
                                queueing
                                  ? 'Research is running; this will join the queue'
                                  : 'Check every entry for duplicates, posts listed as bodies and wrong records, and propose mergers'
                              }
                            >
                              Find duplicates
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto">
                      <StructureTree
                        nodes={nodes}
                        filter={filter}
                        search={search}
                        expanded={expanded}
                        selectedId={selected?.id ?? null}
                        holdersByOrg={holdersByOrg}
                        pendingByOrg={pendingByOrg}
                        recentlyChanged={recentlyChanged}
                        onToggle={toggle}
                        onSelect={select}
                      />
                    </div>
                  </Card>

                  <OutsideList
                    records={structure?.outside ?? []}
                    nodes={nodes}
                    canEdit={canEdit}
                    busy={busy}
                    onResolve={(id, action, target) => void government.resolveOutside(id, action, target)}
                  />
                </div>

                <div className="hidden lg:block">
                  <div className="sticky top-4 h-[calc(100vh-7rem)]">
                    {selected ? (
                      <Card className="h-full overflow-hidden">{inspector}</Card>
                    ) : (
                      <InspectorEmpty />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* ==================== GRAPH ==================== */}
        {tab === 'graph' &&
          (nodes.length === 0 ? (
            <Card className="px-6 py-12 text-center">
              <span className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
                <Network className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <h2 className="mt-3 text-[15px] text-ink">Nothing to draw yet</h2>
              <p className="mx-auto mt-1.5 max-w-md text-[13px] leading-relaxed text-neutral-500">
                Once research has mapped the state, this is where you see it whole — every body,
                where it sits, and who holds its posts.
              </p>
            </Card>
          ) : (
            <div
              className={
                graphScreen.active
                  ? 'fixed inset-0 z-[45] bg-white pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]'
                  : 'relative'
              }
            >
              <GovernmentChart
                nodes={liveNodes}
                countryName={country?.name ?? 'the state'}
                holders={holders}
                updates={pendingList}
                selectedId={selected?.id ?? null}
                loadingPeople={!syncedAt}
                onSelect={select}
                expanded={graphScreen.active}
                onToggleExpanded={graphScreen.toggle}
              />
              {/* On a wide screen the details sit over the chart, so it keeps
                  the whole width; on a narrow one they open as the sheet */}
              {wide && selected && (
                <div className="absolute bottom-16 right-3 top-16 z-20 w-[380px]">
                  <Card className="h-full overflow-hidden shadow-[0_24px_60px_-28px_rgba(0,0,0,0.45)]">
                    {inspectorFor(() => {
                      setSelectedId('');
                      setSheetOpen(false);
                    }, true)}
                  </Card>
                </div>
              )}
            </div>
          ))}

        {/* ==================== UPDATES ==================== */}
        {tab === 'updates' && (
          <div className="space-y-4">
            {/* A row of pills that scrolls sideways hides half of itself on a
                phone, so there the filters are the dropdowns they always were
                in spirit */}
            <div className="grid grid-cols-2 gap-2 sm:hidden">
              <Select
                aria-label="Status"
                value={(government.updateQuery.status ?? 'pending') as UpdateStatus}
                onChange={(event) =>
                  government.setUpdateQuery((query) => ({
                    ...query,
                    status: event.target.value as UpdateStatus,
                  }))
                }
              >
                {STATUS_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Kind"
                value={updateKind}
                onChange={(event) => setUpdateKind(event.target.value as UpdateKind | 'all')}
              >
                {KIND_FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="hidden items-center gap-2 sm:flex sm:flex-wrap">
              <SegmentedControl
                label="Status"
                items={STATUS_FILTERS}
                value={(government.updateQuery.status ?? 'pending') as UpdateStatus}
                onChange={(value) =>
                  government.setUpdateQuery((query) => ({ ...query, status: value as UpdateStatus }))
                }
              />
              <SegmentedControl
                label="Kind"
                items={KIND_FILTERS}
                value={updateKind}
                onChange={(value) => setUpdateKind(value as UpdateKind | 'all')}
              />
            </div>

            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => setResearchOpen(true)}
              >
                Check office holders
              </Button>
            )}

            {canEdit && (formerReady.length > 0 || formerResult) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-neutral-200 bg-white px-4 py-3">
                <p className="min-w-0 flex-1 text-[13px] text-neutral-600">
                  {formerResult
                    ? `${plural(formerResult.applied, 'former holder')} recorded${
                        formerResult.failed ? `, ${formerResult.failed} could not be` : ''
                      }.`
                    : `${plural(formerReady.length, 'former holder')} ready to record at their bodies, with the dates the acts give${
                        visibleUpdates.length > formerReady.length
                          ? `; ${visibleUpdates.length - formerReady.length} need you to say who they are first`
                          : ''
                      }.`}
                </p>
                {formerReady.length > 0 && (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={busy}
                    onClick={async () => {
                      const result = await government.applyUpdates(formerReady.map((update) => update.id));
                      if (result) setFormerResult(result);
                    }}
                  >
                    Record all {formerReady.length}
                  </Button>
                )}
              </div>
            )}

            {visibleUpdates.length === 0 ? (
              <UpdatesEmpty
                filtered={updateKind !== 'all' || government.updateQuery.status !== 'pending'}
                onResearch={canEdit ? () => setResearchOpen(true) : undefined}
              />
            ) : (
              <div className="space-y-6">
                {grouped.map(([label, items]) => (
                  <section key={label}>
                    <div className="mb-2 flex items-center gap-2">
                      <h2 className="min-w-0 truncate text-[13px] text-neutral-500">{label}</h2>
                      <Pill tone="outline">{items.length}</Pill>
                    </div>
                    <div className="space-y-3">
                      {items.map((update: GovUpdateView) => (
                        <UpdateCard
                          key={update.id}
                          update={update}
                          canEdit={canEdit}
                          busy={busy}
                          onApply={(input) => void government.applyUpdate(update.id, input)}
                          onDismiss={(reason) => void government.dismissUpdate(update.id, reason)}
                          onVerify={() => void government.verifyUpdate(update.id)}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== TIMELINE ==================== */}
        {tab === 'timeline' && <TimelinePanel refreshKey={watcher.justFinished?.id ?? null} />}

        {/* ==================== RESEARCH ==================== */}
        {tab === 'research' && (
          <div className="space-y-4">
            {!overview?.researchReady && (
              <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800">
                No provider that can search the web is configured, so research is unavailable.{' '}
                <Link to="/ai-settings" className="underline underline-offset-2">
                  AI settings
                </Link>
              </p>
            )}
            <Card>
              {overview?.runs.length ? (
                <div className="divide-y divide-neutral-100">
                  {overview.runs.map((run) => (
                    <RunRow key={run.id} run={run} onOpen={() => watcher.watch(run)} />
                  ))}
                </div>
              ) : (
                <EmptyState icon={Sparkles} title="No research yet" />
              )}
            </Card>
            {country && (
              <p className="px-1 text-[12px] text-neutral-400">
                Research reads {country.languages.length > 1 ? 'the official record in ' : 'the official record in '}
                {country.languages
                  .map((code) => {
                    try {
                      return new Intl.DisplayNames(undefined, { type: 'language' }).of(code) ?? code;
                    } catch {
                      return code;
                    }
                  })
                  .join(' and ')}
                {country.autoCheck !== 'off'
                  ? ` · appointments are checked ${country.autoCheck}, ministries weekly`
                  : ' · automatic checks are off'}

              </p>
            )}
          </div>
        )}

        {/* ---- apply bar ---- */}
        {tab === 'structure' && canEdit && readyIds.length > 0 && (
          <div
            className="sticky z-20 mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur"
            style={{ bottom: 'calc(var(--app-tabbar-h, 0px) + 12px)' }}
          >
            <Layers className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
            <p className="min-w-0 flex-1 text-[13px] text-ink">
              {applying
                ? `Applying… ${applying.done} of ${applying.total}`
                : `${plural(readyIds.length, 'entity', 'entities')} confirmed and ready`}
            </p>
            <Button
              size="sm"
              variant="primary"
              loading={!!applying}
              onClick={() => void openBulkApply()}

            >
              Apply to the directory
            </Button>
          </div>
        )}

        {/* ---- mobile inspector ---- */}
        {!wide && (
          <Dialog
            open={sheetOpen && !!selected}
            onClose={() => setSheetOpen(false)}
            label="Entity"
            handle
            className="max-h-[88dvh] sm:max-w-lg"
          >
            <div className="flex min-h-0 flex-1 flex-col">{inspector}</div>
          </Dialog>
        )}

        <Dialog
          open={confirmingApply}
          onClose={() => setConfirmingApply(false)}
          label="Apply to the directory"
          handle
          className="sm:max-w-md"
        >
          <DialogHeader
            eyebrow={`${plural(readyIds.length, 'entity', 'entities')} confirmed`}
            title="Apply to the directory"
            onClose={() => setConfirmingApply(false)}
          />
          <DialogBody className="space-y-3">
            {!bulkPlans ? (
              <p className="flex items-center gap-2 text-[13px] text-neutral-500">
                <Spinner className="h-3.5 w-3.5" /> Working out what would change
              </p>
            ) : (
              <>
                <ul className="space-y-1.5 text-[13px] text-ink">
                  {bulk.parentsFirst > 0 && (
                    <li>{plural(bulk.parentsFirst, 'parent body', 'parent bodies')} created first</li>
                  )}
                  {bulk.create > 0 && <li>{plural(bulk.create, 'record')} created</li>}
                  {bulk.recreate > 0 && (
                    <li>{plural(bulk.recreate, 'deleted record')} made again</li>
                  )}
                  {bulk.merged > 0 && (
                    <li>
                      {plural(bulk.merged, 'duplicate')} folded into the record kept for their entity
                      {bulk.people > 0 && `, moving ${plural(bulk.people, 'role')} across`}
                    </li>
                  )}
                  {bulk.official > 0 && (
                    <li>{plural(bulk.official, 'record')} renamed officially, into their name history</li>
                  )}
                  {bulk.corrected > 0 && <li>{plural(bulk.corrected, 'name')} corrected</li>}
                  {bulk.moved > 0 && <li>{plural(bulk.moved, 'record')} filed under the body they belong to</li>}
                  {bulk.plans.length > 0 &&
                    !bulk.create &&
                    !bulk.recreate &&
                    !bulk.merged &&
                    !bulk.official &&
                    !bulk.corrected &&
                    !bulk.moved &&
                    !bulk.heads.length && <li>The records already match the map</li>}
                </ul>

                {bulk.heads.length > 0 && (
                  <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-neutral-200 px-3 py-2.5 text-[13px]">
                    <input
                      type="checkbox"
                      checked={bulkHeads}
                      onChange={(event) => setBulkHeads(event.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600/40"
                    />
                    <span className="min-w-0">
                      <span className="block text-ink">
                        Record the {plural(bulk.heads.length, 'head')} research found
                      </span>
                      <span className="mt-0.5 flex flex-wrap gap-x-3 text-[12px] text-neutral-500">
                        {bulk.newPeople > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <UserPlus className="h-3 w-3" strokeWidth={1.75} />
                            {plural(bulk.newPeople, 'new person', 'new people')}
                          </span>
                        )}
                        {bulk.ending > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <UserMinus className="h-3 w-3" strokeWidth={1.75} />
                            {plural(bulk.ending, 'role')} ended
                          </span>
                        )}
                        <span>each one goes into the Updates history</span>
                      </span>
                    </span>
                  </label>
                )}

                {bulk.blocked.length > 0 && (
                  <p className="flex items-start gap-1.5 text-[12.5px] text-amber-700">
                    <TriangleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                    {plural(bulk.blocked.length, 'entity', 'entities')} will be skipped: {bulk.blocked[0].blocked}
                  </p>
                )}
              </>
            )}
            <p className="text-[12.5px] leading-snug text-neutral-500">
              Folding records together cannot be undone from here. What was folded in is kept in the
              merge history, and every change is written to the audit log.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmingApply(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!bulkPlans}
              onClick={() => {
                setConfirmingApply(false);
                const heads = bulkHeads
                  ? Object.fromEntries(
                      bulk.heads
                        .map((item) => [item.nodeId, defaultHeadChoice(item.head)] as const)
                        .filter((entry): entry is readonly [string, HeadChoice] => !!entry[1]),
                    )
                  : undefined;
                void applyNodes(readyIds, heads).then((results) => setApplied(results));
              }}
            >
              Apply
            </Button>
          </DialogFooter>
        </Dialog>

        {country && (
          <CountryDialog
            open={countryOpen}
            country={country}
            busy={busy}
            onClose={() => setCountryOpen(false)}
            onSave={government.setCountry}
          />
        )}
        {country && (
          <ResearchDialog
            open={researchOpen}
            countryName={country.name}
            researchReady={!!overview?.researchReady}
            queueing={queueing}
            busy={busy}
            onClose={() => setResearchOpen(false)}
            onStart={(input) => void government.start(input)}
          />
        )}
      </Page>
    </DashboardLayout>
  );
};

export default GovernmentPage;
